import { DialogComponent, DialogOpenEvent } from "@theme/dialog";
import { CartAddEvent, CartUpdateEvent } from "@theme/events";

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener("scroll", this.#handleContentScroll, {
      capture: true,
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );
    this.removeEventListener("scroll", this.#handleContentScroll, {
      capture: true,
    });
  }

  /**
   * @param {Event} event
   */
  #handleCartAdd = (event) => {
    if (
      event instanceof CartUpdateEvent &&
      event.detail?.sourceId === "gift-rules-sync"
    ) {
      return;
    }

    if (this.hasAttribute("auto-open")) {
      this.showDialog();
    }
  };

  open() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog?.open) return;

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms"
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta"
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      "cart-summary-sticky",
      ratio > this.#summaryThreshold ? "false" : "true"
    );
  }

  /**
   * Toggles the header shadow once the cart content has been scrolled down,
   * and hides the footer shadow once scrolled to the bottom — so each shadow
   * only implies content in the direction it's actually hiding.
   *
   * @param {Event} event
   */
  #handleContentScroll(event) {
    if (
      !(event.target instanceof Element) ||
      !event.target.matches(".cart-drawer__items, .cart-drawer__content")
    ) {
      return;
    }

    const { dialog } = /** @type {Refs} */ (this.refs);
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1; // 1px tolerance for subpixel rounding

    const header = dialog?.querySelector(".cart-drawer__header");
    header?.classList.toggle("is-scrolled", scrollTop > 0);

    const summary = dialog?.querySelector(".cart-drawer__summary");
    summary?.classList.toggle("is-at-bottom", atBottom);
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}